import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import logger from "../../utils/logger";
import { CheckCircle, Circle, Shield, AlertTriangle, Clock, UserCheck } from "lucide-react";

const API = `${window.location.origin}/api`;

const FACTOR_COLORS = { high: "#ef4444", medium: "#f59e0b", low: "#10b981" };

export function CDDPanel({ customerId }) {
  const [cddData, setCddData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCDD = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/customers/${customerId}/cdd`, { withCredentials: true });
      setCddData(data);
    } catch (err) {
      logger.error("Failed to fetch CDD data:", err);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { fetchCDD(); }, [fetchCDD]);

  const toggleItem = async (itemId, currentCompleted) => {
    try {
      await axios.put(`${API}/customers/${customerId}/cdd/${itemId}`, { completed: !currentCompleted }, { withCredentials: true });
      setCddData(prev => ({
        ...prev,
        items: prev.items.map(i => i.id === itemId ? { ...i, completed: !i.completed } : i),
      }));
    } catch (err) {
      logger.error("Failed to update CDD item:", err);
    }
  };

  const approveEDD = async () => {
    try {
      await axios.put(`${API}/customers/${customerId}/edd-approve`, {}, { withCredentials: true });
      setCddData(prev => ({ ...prev, edd_approved: true }));
    } catch (err) {
      logger.error("Failed to approve EDD:", err);
    }
  };

  if (loading) return <div style={{ padding: "20px", color: "#475569" }}>Loading CDD data...</div>;
  if (!cddData) return null;

  const cddItems = cddData.items?.filter(i => i.category === "cdd") || [];
  const eddItems = cddData.items?.filter(i => i.category === "edd") || [];
  const completedCount = cddData.items?.filter(i => i.completed).length || 0;
  const totalCount = cddData.items?.length || 0;
  const completionPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div data-testid="cdd-panel" className="space-y-4">
      {/* CDD Progress */}
      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "10px", padding: "16px" }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Shield style={{ width: "14px", height: "14px", color: "#2563eb" }} />
            <span style={{ fontSize: "13px", fontWeight: 700, color: "#f1f5f9" }}>Due Diligence Checklist</span>
          </div>
          <span style={{ fontSize: "12px", fontWeight: 700, color: completionPct === 100 ? "#10b981" : "#f59e0b" }}>
            {completedCount}/{totalCount} ({completionPct}%)
          </span>
        </div>
        <div style={{ width: "100%", height: "4px", background: "#1e2530", borderRadius: "2px", overflow: "hidden" }}>
          <div style={{ width: `${completionPct}%`, height: "100%", background: completionPct === 100 ? "#10b981" : "#2563eb", transition: "width 0.3s" }} />
        </div>
      </div>

      {/* CDD Items */}
      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "10px", padding: "14px" }}>
        <div style={{ fontSize: "10px", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>
          Standard CDD
        </div>
        {cddItems.map(item => (
          <button key={item.id} data-testid={`cdd-item-${item.id}`} onClick={() => toggleItem(item.id, item.completed)} style={{
            display: "flex", alignItems: "center", gap: "10px", width: "100%", padding: "8px 4px",
            background: "transparent", border: "none", cursor: "pointer", borderBottom: "1px solid #0f1520",
          }}>
            {item.completed
              ? <CheckCircle style={{ width: "16px", height: "16px", color: "#10b981", flexShrink: 0 }} />
              : <Circle style={{ width: "16px", height: "16px", color: "#475569", flexShrink: 0 }} />
            }
            <span style={{ fontSize: "12px", color: item.completed ? "#10b981" : "#f1f5f9", textDecoration: item.completed ? "line-through" : "none", textAlign: "left" }}>
              {item.label}
            </span>
            {item.completed_by && (
              <span style={{ fontSize: "9px", color: "#475569", marginLeft: "auto" }}>{item.completed_by}</span>
            )}
          </button>
        ))}
      </div>

      {/* EDD Section */}
      {cddData.needs_edd && (
        <div style={{ background: "#0d1117", border: "1px solid rgba(239,68,68,0.2)", borderRadius: "10px", padding: "14px" }}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <AlertTriangle style={{ width: "14px", height: "14px", color: "#ef4444" }} />
              <span style={{ fontSize: "12px", fontWeight: 700, color: "#ef4444" }}>Enhanced Due Diligence Required</span>
            </div>
            {cddData.edd_approved ? (
              <span style={{ fontSize: "9px", fontWeight: 700, padding: "2px 8px", borderRadius: "4px", background: "rgba(16,185,129,0.12)", color: "#10b981" }}>
                APPROVED
              </span>
            ) : (
              <button onClick={approveEDD} data-testid="edd-approve-btn" style={{
                background: "rgba(37,99,235,0.12)", border: "1px solid rgba(37,99,235,0.25)",
                borderRadius: "6px", padding: "4px 12px", cursor: "pointer", color: "#60a5fa", fontSize: "10px", fontWeight: 600,
              }}>
                Approve EDD
              </button>
            )}
          </div>
          {eddItems.map(item => (
            <button key={item.id} data-testid={`edd-item-${item.id}`} onClick={() => toggleItem(item.id, item.completed)} style={{
              display: "flex", alignItems: "center", gap: "10px", width: "100%", padding: "8px 4px",
              background: "transparent", border: "none", cursor: "pointer", borderBottom: "1px solid #0f1520",
            }}>
              {item.completed
                ? <CheckCircle style={{ width: "16px", height: "16px", color: "#10b981", flexShrink: 0 }} />
                : <Circle style={{ width: "16px", height: "16px", color: "#ef4444", flexShrink: 0 }} />
              }
              <span style={{ fontSize: "12px", color: item.completed ? "#10b981" : "#f1f5f9", textAlign: "left" }}>
                {item.label}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Risk Score Breakdown */}
      {cddData.risk_breakdown && (
        <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "10px", padding: "14px" }}>
          <div className="flex items-center gap-2 mb-3">
            <UserCheck style={{ width: "14px", height: "14px", color: "#FFD700" }} />
            <span style={{ fontSize: "12px", fontWeight: 700, color: "#f1f5f9" }}>Risk Score Breakdown</span>
          </div>
          {cddData.risk_breakdown.map(factor => (
            <div key={factor.factor} data-testid={`risk-factor-${factor.factor.toLowerCase().replace(/\s/g, "-")}`}
              style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px 0", borderBottom: "1px solid #0f1520" }}>
              <span style={{ fontSize: "11px", color: "#94a3b8", width: "110px" }}>{factor.factor}</span>
              <div style={{ flex: 1, height: "4px", background: "#1e2530", borderRadius: "2px", overflow: "hidden" }}>
                <div style={{ width: `${Math.min(100, factor.score * 3)}%`, height: "100%", background: FACTOR_COLORS[factor.level], borderRadius: "2px" }} />
              </div>
              <span style={{ fontSize: "10px", fontWeight: 700, color: FACTOR_COLORS[factor.level], width: "30px", textAlign: "right" }}>
                {factor.score}
              </span>
              <span style={{ fontSize: "9px", color: "#475569", width: "80px", textAlign: "right" }}>{factor.detail}</span>
            </div>
          ))}
        </div>
      )}

      {/* Periodic Review */}
      {cddData.periodic_review_due && (
        <div className="flex items-center gap-2" style={{ fontSize: "11px", color: "#475569" }}>
          <Clock style={{ width: "12px", height: "12px" }} />
          Periodic Review Due: {new Date(cddData.periodic_review_due).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
        </div>
      )}
    </div>
  );
}
